import { UserProfile } from "@/redux/types";
import {
  getHeadersWithUniqueVisitorId,
  getVisitorIdCookie,
} from "../../auth/Auth";
import { HOST_DOMAIN } from "../../utils/domain";
import { IAppInstallationInfo } from "@brandclub/common-ui";

const DOMAIN = HOST_DOMAIN + "/api-rewards";

// debounce api call to avoid multiple requests based on the args
// if the args are the same, the function will return the same result
function debounceApiCall(func: (...args: any[]) => any, wait: number) {
  const cache = new Map<string, any>();
  return (...args: any[]) => {
    const key = JSON.stringify(args);
    if (cache.has(key)) {
      return cache.get(key);
    }
    const result = func(...args);
    cache.set(key, result);
    setTimeout(() => cache.delete(key), wait);
    return result;
  };
}

export const getUserProfile = async (
  signal?: AbortSignal
): Promise<UserProfile> => {
  const headers = await getHeadersWithUniqueVisitorId();
  if (!headers.Authorization) {
    throw new Error("No token available");
  }
  const res = await fetch(DOMAIN + `/profile/me?refreshCache=true&cue=false`, {
    method: "GET",
    headers,
    signal,
  });

  if (res.ok) {
    return await res.json();
  } else {
    throw new Error("Error fetching user profile");
  }
};

export const refreshAccessToken = debounceApiCall(
  async ({
    refreshToken,
    clientId,
  }: {
    refreshToken: string;
    clientId: string;
  }) => {
    const res = await fetch(DOMAIN + `/auth/refreshAccessToken`, {
      method: "POST",
      headers: {
        "x-bc-visitor-id": getVisitorIdCookie(),
        "Content-type": "application/json",
      },
      body: JSON.stringify({ refreshToken, clientId }),
    });

    if (res.ok) {
      return await res.json();
    } else {
      throw new Error("Error refreshing access token");
    }
  },
  2000
);

export const updateAppInstallationInfoInCheckoutBar = async (
  appInstallationInfo: IAppInstallationInfo,
  signal?: AbortSignal
): Promise<any> => {
  const headers = await getHeadersWithUniqueVisitorId();
  if (!headers.Authorization) {
    return { status: "error", message: "No token available" };
  }
  const res = await fetch(DOMAIN + `/appInstallation/update`, {
    method: "POST",
    headers,
    body: JSON.stringify(appInstallationInfo),
    signal,
  });

  if (res.ok) {
    return await res.json();
  } else {
    throw new Error("Error updating app installation info");
  }
};
